import React, { useState, useEffect, useCallback, useRef } from 'react';
import heic2any from 'heic2any';
import './FirmProfile.css';
import { apiCall } from '../utils/api';

const FirmProfile = ({ onLogout }) => {
    const [firmData, setFirmData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [firmProfile, setFirmProfile] = useState(''); // List[str] - areas of expertise
    const [firmStrength, setFirmStrength] = useState(''); // int - team size
    const [partners, setPartners] = useState([]);
    const [masterPartners, setMasterPartners] = useState([]);
    const [newPartner, setNewPartner] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [photos, setPhotos] = useState([]);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [logoUploadError, setLogoUploadError] = useState('');
    const [photoUploadError, setPhotoUploadError] = useState('');
    const [photoDisplayUrls, setPhotoDisplayUrls] = useState({});
    const [logoDisplayUrl, setLogoDisplayUrl] = useState('');
    const photoPreviewUrlsRef = useRef({});
    const logoPreviewRef = useRef('');

    const extractApiMessage = (payload) => {
        if (!payload) return '';

        const candidates = [
            payload.response?.data?.error,
            payload.response?.data?.message,
            payload.data?.error,
            payload.data?.message,
            payload.error,
            payload.message
        ];

        return candidates.find(Boolean) || '';
    };

    const isHeicFile = (file) => {
        if (!file) return false;
        const lowerName = file.name?.toLowerCase() || '';

        return file.type === 'image/heic' || lowerName.endsWith('.heic');
    };

    const isHeicUrl = (url) => {
        if (!url) return false;
        const stripped = url.split('?')[0].toLowerCase();
        return stripped.endsWith('.heic');
    };

    const prepareFileForUpload = async (file) => {
        if (!isHeicFile(file)) {
            return file;
        }

        try {
            const convertedBlob = await heic2any({
                blob: file,
                toType: 'image/jpeg',
                quality: 0.9
            });

            if (convertedBlob instanceof Blob) {
                const convertedName = file.name.replace(/\.heic$/i, '.jpg');
                return new File([convertedBlob], convertedName, { type: 'image/jpeg' });
            }
        } catch (conversionError) {
            console.warn('HEIC conversion failed, uploading original file', conversionError);
        }

        return file;
    };

    const fetchMasterPartners = useCallback(async (firmName, currentPartners) => {
        try {
            const response = await apiCall.get('/firm/masters');
            const masters = response.data || response || [];
            if (Array.isArray(masters)) {
                const match = masters.find(f =>
                    f.firm_name?.toLowerCase() === firmName?.toLowerCase()
                );
                if (match?.partners?.length) {
                    setMasterPartners(match.partners);
                    const merged = [...currentPartners];
                    match.partners.forEach(p => {
                        if (!merged.includes(p)) merged.push(p);
                    });
                    setPartners(merged);
                }
            }
        } catch (e) {
            console.error('Error fetching firm masters:', e);
        }
    }, []);

    const fetchFirmProfile = useCallback(async () => {
        try {
            const response = await apiCall.get('/firm/me');
            const data = response.data || response;
            if (data) {
                setFirmData(data);
                // firm_profile is List[str], display as comma-separated
                const profileArr = data.firm_profile || data.expertise || [];
                setFirmProfile(Array.isArray(profileArr) ? profileArr.join(', ') : profileArr || '');
                // firm_strength is int (team size)
                setFirmStrength(data.firm_strength?.toString() || '');
                setLogoUrl(data.logo_url || '');
                setPhotos(data.photos || []);

                const partnerSources = [
                    data.partners || [],
                    data.partner_names || []
                ];
                const partnerSet = new Set();
                partnerSources.forEach(source => {
                    if (Array.isArray(source)) {
                        source.forEach(p => {
                            if (p) partnerSet.add(p.trim());
                        });
                    } else if (typeof source === 'string') {
                        source.split(',')
                            .map(p => p.trim())
                            .filter(Boolean)
                            .forEach(p => partnerSet.add(p));
                    }
                });

                const partnerList = Array.from(partnerSet);
                setPartners(partnerList);
                fetchMasterPartners(data.firm_name, partnerList);
            }
        } catch (error) {
            console.error('Error fetching firm profile:', error);
        } finally {
            setLoading(false);
        }
    }, [fetchMasterPartners]);

    useEffect(() => {
        fetchFirmProfile();
    }, [fetchFirmProfile]);

    useEffect(() => {
        let active = true;

        if (!photos.length) {
            setPhotoDisplayUrls({});
        } else {
            const processPhotos = async () => {
                const entries = await Promise.all(photos.map(async (url) => {
                    if (!url) return null;
                    if (!isHeicUrl(url)) {
                        return [url, url];
                    }

                    try {
                        const response = await fetch(url);
                        const blob = await response.blob();
                        const convertedBlob = await heic2any({
                            blob,
                            toType: 'image/jpeg',
                            quality: 0.9
                        });

                        if (convertedBlob instanceof Blob) {
                            const previewUrl = URL.createObjectURL(convertedBlob);
                            photoPreviewUrlsRef.current[url] = previewUrl;
                            return [url, previewUrl];
                        }
                    } catch (err) {
                        console.error('HEIC conversion failed:', err);
                    }

                    return [url, url];
                }));

                if (!active) return;

                const map = {};
                entries.forEach((entry) => {
                    if (entry) {
                        map[entry[0]] = entry[1];
                    }
                });
                setPhotoDisplayUrls(map);
            };

            processPhotos();
        }

        return () => {
            active = false;
            Object.values(photoPreviewUrlsRef.current).forEach(URL.revokeObjectURL);
            photoPreviewUrlsRef.current = {};
        };
    }, [photos]);

    useEffect(() => {
        if (!logoUrl) {
            setLogoDisplayUrl('');
            return;
        }

        if (!isHeicUrl(logoUrl)) {
            setLogoDisplayUrl(logoUrl);
            return;
        }

        let active = true;

        const convertLogo = async () => {
            try {
                const response = await fetch(logoUrl);
                const blob = await response.blob();
                const convertedBlob = await heic2any({
                    blob,
                    toType: 'image/jpeg',
                    quality: 0.9
                });

                if (convertedBlob instanceof Blob) {
                    const previewUrl = URL.createObjectURL(convertedBlob);
                    logoPreviewRef.current = previewUrl;
                    if (active) {
                        setLogoDisplayUrl(previewUrl);
                    }
                    return;
                }
            } catch (err) {
                console.error('HEIC logo conversion failed:', err);
            }

            if (active) {
                setLogoDisplayUrl(logoUrl);
            }
        };

        convertLogo();

        return () => {
            active = false;
            if (logoPreviewRef.current) {
                URL.revokeObjectURL(logoPreviewRef.current);
                logoPreviewRef.current = '';
            }
        };
    }, [logoUrl]);

    const addPartner = () => {
        const name = newPartner.trim();
        if (!name || partners.includes(name)) return;
        setPartners(prev => [...prev, name]);
        setNewPartner('');
    };

    const removePartner = (name) => {
        setPartners(prev => prev.filter(p => p !== name));
    };

    const addMasterPartner = (name) => {
        if (partners.includes(name)) return;
        setPartners(prev => [...prev, name]);
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Reset input so same file can be selected again
        e.target.value = '';

        setLogoUploadError('');
        setUploadingLogo(true);
        try {
            const formData = new FormData();
            const uploadFile = await prepareFileForUpload(file);
            formData.append('file', uploadFile);

            const response = await apiCall.post('/firm/upload/logo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            console.log('Logo upload full response:', JSON.stringify(response, null, 2));
            
            const responseError = extractApiMessage(response);
            if (responseError) {
                setLogoUploadError(responseError);
                return;
            }

            // Try all possible paths to find public_url
            const publicUrl = response?.data?.public_url 
                || response?.public_url 
                || response?.data?.data?.public_url;
            
            console.log('Extracted logo URL:', publicUrl);
            
            if (publicUrl) {
                const urlWithTimestamp = `${publicUrl}?t=${Date.now()}`;
                console.log('Setting logo URL to:', urlWithTimestamp);
                setLogoUrl(urlWithTimestamp);
                console.log('logoUrl state updated');
                const updatedPhotos = photos.includes(urlWithTimestamp) ? photos : [...photos, urlWithTimestamp];
                if (updatedPhotos.length !== photos.length) {
                    setPhotos(updatedPhotos);
                }
                await persistProfile(updatedPhotos);
            } else {
                console.error('No public_url found. Response keys:', Object.keys(response || {}));
                if (response?.data) {
                    console.error('response.data keys:', Object.keys(response.data || {}));
                }
            }
        } catch (err) {
            console.error('Logo upload error:', err);
            const errorMessage = extractApiMessage(err) || 'Failed to upload logo. Please try again.';
            setLogoUploadError(errorMessage);
        } finally {
            setUploadingLogo(false);
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Reset input so same file can be selected again
        e.target.value = '';

        setPhotoUploadError('');
        setUploadingPhoto(true);
        try {
            const formData = new FormData();
            const uploadFile = await prepareFileForUpload(file);
            formData.append('file', uploadFile);

            const response = await apiCall.post('/firm/upload/photos', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            console.log('Photo upload response:', response);
            
            const responseError = extractApiMessage(response);
            if (responseError) {
                setPhotoUploadError(responseError);
                return;
            }

            // Response is: { message, status, data: { public_url } }
            const publicUrl = response.data?.public_url || response.public_url;
            console.log('Extracted photo URL:', publicUrl);
            
            if (publicUrl) {
                const urlWithTimestamp = `${publicUrl}?t=${Date.now()}`;
                setPhotoUploadError('');
                const updatedPhotos = [...photos, urlWithTimestamp];
                setPhotos(updatedPhotos);
                await persistProfile(updatedPhotos);
            } else {
                console.error('No public_url found in response');
            }
        } catch (err) {
            console.error('Photo upload error:', err);
            const errorMessage = extractApiMessage(err) || 'Failed to upload photo. Please try again.';
            setPhotoUploadError(errorMessage);
        } finally {
            setUploadingPhoto(false);
        }
    };

    const removePhoto = async (url) => {
        const previousPhotos = photos;
        setPhotos(prev => prev.filter(p => p !== url));

        try {
            const response = await apiCall.delete('/firm/photos', {
                data: { photo_url: url }
            });
            const updatedPhotos = response.data?.photos ?? response.photos;
            if (Array.isArray(updatedPhotos)) {
                setPhotos(updatedPhotos);
            }
        } catch (err) {
            console.error('Error deleting photo:', err);
            setPhotos(previousPhotos);
        }
    };

    const normalizeListDescription = (inputValue) => {
        if (!inputValue) return [];
        const trimmed = inputValue.trim();
        if (!trimmed) return [];

        const isBracketArray = trimmed.startsWith('[') && trimmed.endsWith(']');
        if (isBracketArray) {
            const inner = trimmed.slice(1, -1);
            const cleaned = inner
                .replace(/\\n/g, ' ')
                .replace(/\+/g, ' ')
                .replace(/['"]/g, ' ')
                .replace(/\s+/g, ' ');
            const parts = cleaned.split(/,|\r?\n/).map(part => part.trim()).filter(Boolean);
            if (parts.length > 0) {
                return [parts.join('\n')];
            }
            return [];
        }

        return [trimmed];
    };

    const buildProfilePayload = (photoList = photos) => {
        const profileArray = normalizeListDescription(firmProfile);
        const strengthInt = parseInt(firmStrength) || 0;
        const allPartners = [...partners];
        if (newPartner.trim() && !allPartners.includes(newPartner.trim())) {
            allPartners.push(newPartner.trim());
        }

        return {
            firm_profile: profileArray,
            firm_strength: strengthInt,
            partners: allPartners,
            partner_names: allPartners,
            logo_url: logoUrl,
            photos: photoList
        };
    };

    const persistProfile = async (photoList = photos) => {
        try {
            await apiCall.put('/firm/profile', JSON.stringify(buildProfilePayload(photoList)));
        } catch (error) {
            console.error('Error syncing profile:', error);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveMessage('');
        try {
            const allPartners = [...partners];
            if (newPartner.trim() && !allPartners.includes(newPartner.trim())) {
                allPartners.push(newPartner.trim());
            }

            await persistProfile();
            
            // Clear the new partner input after successful save
            if (newPartner.trim()) {
                setPartners(allPartners);
                setNewPartner('');
            }
            
            setSaveMessage('Saved');
            setTimeout(() => setSaveMessage(''), 2000);
        } catch (error) {
            console.error('Error updating profile:', error);
            setSaveMessage('Failed to save');
            setTimeout(() => setSaveMessage(''), 2000);
        } finally {
            setSaving(false);
        }
    };

    const handleLogout = () => {
        document.cookie = 'access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT';
        document.cookie = 'refresh_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT';
        localStorage.removeItem('user_type');
        onLogout && onLogout();
    };

    const unusedMasterPartners = masterPartners.filter(p => !partners.includes(p));

    if (loading) {
        return (
            <div className="firm-profile-container">
                <div className="firm-profile-loading">Loading...</div>
            </div>
        );
    }

    console.log('Rendering with logoUrl:', logoUrl);
    
    return (
        <div className="firm-profile-container">
            <div className="firm-profile-header">
                <h1>Firm Profile</h1>
                <div className="firm-profile-card">
                    <label className="firm-profile-avatar-wrapper">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            disabled={uploadingLogo}
                            style={{ display: 'none' }}
                        />
                        {logoDisplayUrl ? (
                            <img 
                                src={logoDisplayUrl} 
                                alt="Firm Logo" 
                                className="firm-profile-avatar-img"
                            />
                        ) : (
                            <div className="firm-profile-avatar">
                                {uploadingLogo ? '...' : (firmData?.firm_name || 'F').charAt(0).toUpperCase()}
                            </div>
                        )}
                        <span className="firm-profile-avatar-edit">📷</span>
                    </label>
                    <h2 className="firm-profile-name">{firmData?.firm_name}</h2>
                    {firmData?.address && (
                        <p className="firm-profile-address">{firmData.address}</p>
                    )}
                </div>
                {logoUploadError && (
                    <div className="fp-upload-error fp-upload-error--logo">
                        {logoUploadError}
                    </div>
                )}
            </div>

            <div className="firm-profile-section">

                {/* Photos Upload */}
                <div className="fp-edit-field">
                    <label>Firm Photos</label>
                    <p className="fp-photo-hint">Do not exceed 5 MB per file.</p>
                    <div className="fp-photos-grid">
                        {photos.map((url, i) => {
                            const isHeic = isHeicUrl(url);
                            const displaySrc = photoDisplayUrls[url] || (isHeic ? '' : url);

                            return (
                                <div key={`${url || 'photo'}-${i}`} className="fp-photo-item">
                                    {displaySrc ? (
                                        <img
                                            src={displaySrc}
                                            alt={`${firmData?.firm_name || 'Firm'} view ${i + 1}`}
                                            onError={(e) => {
                                                console.log('Photo failed to load:', url);
                                                e.target.parentElement.style.display = 'none';
                                            }}
                                        />
                                    ) : (
                                        <div className="fp-photo-placeholder">
                                            {isHeic ? 'Converting HEIC...' : 'Loading...'}
                                        </div>
                                    )}
                                    <button className="fp-photo-remove" onClick={() => removePhoto(url)}>×</button>
                                </div>
                            );
                        })}
                        <label className="fp-photo-add">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoUpload}
                                disabled={uploadingPhoto}
                                style={{ display: 'none' }}
                            />
                            {uploadingPhoto ? '...' : '+'}
                        </label>
                    </div>
                    {photoUploadError && (
                        <div className="fp-upload-error fp-upload-error--photo">
                            {photoUploadError}
                        </div>
                    )}
                </div>

                <div className="fp-edit-field">
                    <label>Firm Profile (Areas of Expertise)</label>
                    <textarea
                        value={firmProfile}
                        onChange={(e) => setFirmProfile(e.target.value)}
                        placeholder="e.g. Audit, Taxation, GST, Company Law..."
                        rows={3}
                    />
                </div>

                <div className="fp-edit-field">
                    <label>Firm Strength (Team Size)</label>
                    <input
                        type="number"
                        min="1"
                        value={firmStrength}
                        onChange={(e) => setFirmStrength(e.target.value)}
                        placeholder="e.g. 10"
                        className="fp-input"
                    />
                </div>

                <div className="fp-edit-field">
                    <label>Partner Names</label>
                    <div className="fp-partners-chips">
                        {partners.map((p, i) => (
                            <span key={i} className="fp-partner-chip">
                                {p}
                                <button onClick={() => removePartner(p)} aria-label={`Remove ${p}`}>×</button>
                            </span>
                        ))}
                    </div>

                    {unusedMasterPartners.length > 0 && (
                        <div className="fp-master-suggestions">
                            <span className="fp-suggestions-label">From firm records:</span>
                            <div className="fp-suggestions-list">
                                {unusedMasterPartners.map((p, i) => (
                                    <button
                                        key={i}
                                        className="fp-suggestion-chip"
                                        onClick={() => addMasterPartner(p)}
                                    >
                                        + {p}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="fp-add-partner">
                        <input
                            type="text"
                            value={newPartner}
                            onChange={(e) => setNewPartner(e.target.value)}
                            placeholder="Add a partner name..."
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPartner())}
                        />
                        <button onClick={addPartner} disabled={!newPartner.trim()}>Add</button>
                    </div>
                </div>

                <button 
                    className="fp-save-btn"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
                {saveMessage && (
                    <div className={`fp-save-message ${saveMessage === 'Saved' ? 'success' : 'error'}`}>
                        {saveMessage}
                    </div>
                )}

                <button className="firm-profile-logout" onClick={handleLogout}>
                    Logout
                </button>
            </div>
        </div>
    );
};

export default FirmProfile;
